import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useForecastingSettings } from './forecasting-settings-provider';
import { emptyArray } from 'ui-components/default-props/empty';
import Global from '../../../store/global-selectors';
import { useOrderVelocity } from '../hooks/use-order-velocity';
import { calculateSuggestedReorder } from '../utils/calculate-suggested-reorder';

/**
 * @typedef {import('../types').MonthlyOrder} MonthlyOrder
 * @typedef {import('../types').InventoryItemFromEntity} InventoryItemFromEntity
 * @typedef {import('../types').SkuVelocityDataItem} SkuVelocityDataItem
 * @typedef {import('../types').OrderVelocityData} OrderVelocityData
 * @typedef {import('../types').QuantityValuesMap} QuantityValuesMap
 * @typedef {import('../types').ForecastingModifiers} ForecastingModifiers
 */

export const ForecastingDataContext = createContext();

/**
 * Process inventory data with API data
 * @param {Array<InventoryItemFromEntity>} entities - Array of inventory entities
 * @param {OrderVelocityData} orderVelocityData - Order velocity data from API containing historical sales information
 * @param {string} forecastPeriod - Forecast period
 * @param {object} modifiers - Forecasting calculation modifiers object
 * @param {number} [modifiers.salesTrend] - Sales trend percentage adjustment (-100 to 100)
 * @param {number} [modifiers.salesTrendModifier] - Legacy sales trend modifier (kept for backward compatibility)
 * @param {number} [modifiers.leadTimeDays] - Lead time in days for supplier delivery
 * @param {number} [modifiers.bufferStockDays] - Additional buffer stock days to maintain
 * @param {object} quantityValues - Map object where keys are entity IDs (strings/numbers) and values are user-specified quantity amounts (numbers)
 * @param {string} [inventoryWarehouseId] - Optional warehouse ID to filter inventory entities
 * @returns {Array} Processed data
 */
const processInventoryData = (entities, orderVelocityData, forecastPeriod, modifiers, quantityValues, inventoryWarehouseId) => {
  const entitiesBySku = entities.reduce((acc, entity) => {
    if (!entity.sku) return acc;

    if (inventoryWarehouseId && entity.inventoryWarehouseId !== inventoryWarehouseId) {
      return acc;
    }

    const existingGroup = acc[entity.sku];
    return {
      ...acc,
      [entity.sku]: {
        entities: existingGroup ? [...existingGroup.entities, entity] : [entity],
        totalOnHand: (existingGroup?.totalOnHand || 0) + (entity.stock || 0),
      },
    };
  }, {});

  return Object.keys(entitiesBySku).map((sku) => {
    const group = entitiesBySku[sku];
    const baseEntity = group.entities[0];
    const skuData = orderVelocityData?.data?.find((item) => item.skuId === sku) || null;

    // Default values
    let avgOrders = 0;
    let hasChartData = false;
    const hasSufficientHistoricalData = Boolean(skuData);
    let monthlyOrdersData = [];

    if (skuData) {
      avgOrders = Math.round(skuData.averageMonthlyVelocity);
      hasChartData = Boolean(skuData.monthlyOrders && skuData.monthlyOrders.length > 0);
      monthlyOrdersData = skuData.monthlyOrders || [];
    }

    const { modifiedSuggestedAmount } = calculateSuggestedReorder(
      {
        ...baseEntity,
        stock: group.totalOnHand,
      },
      forecastPeriod,
      modifiers,
      avgOrders,
    );

    return {
      id: baseEntity.id,
      sku,
      productName: baseEntity.productName || '',
      currentOnHand: group.totalOnHand,
      reorderThreshold: baseEntity.reorderThreshold || 0,
      avgOrders,
      suggestedReorder: modifiedSuggestedAmount,
      quantity: quantityValues[baseEntity.id] || modifiedSuggestedAmount,
      imageUrl: baseEntity.productThumbnailUrl || '',
      hasChartData,
      hasSufficientHistoricalData,
      monthlyOrdersData,
      velocityVariability: skuData?.velocityVariability,
      limitedData: skuData?.limitedData || false,
      firstOrderDate: skuData?.firstOrderDate,
      lastOrderDate: skuData?.lastOrderDate,
      totalOrdersInPeriod: skuData?.totalOrdersInPeriod,
    };
  });
};

/**
 * Provider component for forecasting data
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string[]} [props.selectedProductIds] - Array of selected product IDs to filter by
 * @param {string} [props.inventoryWarehouseId] - Optional warehouse ID to filter inventory entities
 * @returns {React.ReactElement} The provider component
 */
export const ForecastingDataProvider = ({ children, selectedProductIds = emptyArray, inventoryWarehouseId }) => {
  const [forecastingData, setForecastingData] = useState([]);
  const [rawEntities, setRawEntities] = useState([]);

  const { settings } = useForecastingSettings();
  const { forecastPeriod, lookbackPeriod, salesTrend, leadTimeDays, bufferStockDays } = settings;

  const modifiers = useMemo(
    () => ({
      salesTrendModifier: salesTrend,
      leadTimeDays,
      bufferStockDays,
    }),
    [salesTrend, leadTimeDays, bufferStockDays],
  );

  const [data, setData] = useState([]);
  const [quantityValues, setQuantityValues] = useState({});
  const quantityValuesRef = useRef(quantityValues);

  // Keep ref in sync with state
  quantityValuesRef.current = quantityValues;

  const updateData = useCallback((rowId, value) => {
    setQuantityValues((prev) => ({
      ...prev,
      [rowId]: value,
    }));
  }, []);

  const handleDataLoaded = useCallback((processedData, entities) => {
    setForecastingData(processedData);
    setRawEntities(entities);
  }, []);

  const inventoryEntities = useSelector((state) =>
    Global.selectEntitiesInventoryByProductIds(state, {
      productIds: selectedProductIds,
    }),
  );

  const { orderVelocityData, apiLoading, error } = useOrderVelocity(inventoryEntities, lookbackPeriod);

  useEffect(() => {
    if (inventoryEntities.length === 0) {
      return;
    }

    if (apiLoading) {
      return;
    }

    let processedData = [];
    processedData = processInventoryData(
      inventoryEntities,
      orderVelocityData,
      forecastPeriod,
      modifiers,
      quantityValuesRef.current,
      inventoryWarehouseId,
    );
    setData(processedData);
    handleDataLoaded(processedData, inventoryEntities);

    if (processedData && processedData.length > 0) {
      const currentQuantityValues = quantityValuesRef.current;
      const newQuantities = processedData.reduce(
        (acc, item) => ({
          ...acc,
          [item.id]: item.suggestedReorder,
        }),
        {},
      );

      const hasChanges = Object.keys(newQuantities).some((id) => newQuantities[id] !== currentQuantityValues[id]);

      if (hasChanges) {
        setQuantityValues(newQuantities);
      }
    }
  }, [
    forecastPeriod,
    lookbackPeriod,
    modifiers,
    inventoryEntities,
    handleDataLoaded,
    salesTrend,
    leadTimeDays,
    bufferStockDays,
    orderVelocityData,
    apiLoading,
    inventoryWarehouseId,
  ]);

  const contextValue = useMemo(
    () => ({
      data,
      isLoading: apiLoading,
      error,
      quantityValues,
      updateData,
      orderVelocityData,
      lookbackPeriod,
      forecastPeriod,
      salesTrend,
      leadTimeDays,
      bufferStockDays,
      forecastingData,
      rawEntities,
    }),
    [
      data,
      apiLoading,
      error,
      quantityValues,
      updateData,
      orderVelocityData,
      lookbackPeriod,
      forecastPeriod,
      salesTrend,
      leadTimeDays,
      bufferStockDays,
      forecastingData,
      rawEntities,
    ],
  );

  return <ForecastingDataContext.Provider value={contextValue}>{children}</ForecastingDataContext.Provider>;
};
